/**
 * Comments and tracked changes: editor decorations plus the Yjs operations
 * the review panel uses (accept, reject, comment, reply, resolve).
 */
import { Facet, RangeSetBuilder, StateEffect, StateField, type Extension } from "@codemirror/state";
import { Decoration, EditorView, ViewPlugin, WidgetType, keymap, showTooltip, type DecorationSet, type Tooltip, type ViewUpdate } from "@codemirror/view";
import * as Y from "yjs";
import { userColor } from "../lib/app";
import { changesMap, decodeRel, encodeRel, newId, reviewAction, threadsMap, type ChangeRecord, type ReviewUser } from "./collab";

export interface ThreadMessage {
  id: string;
  userId: number;
  userName: string;
  text: string;
  ts: number;
  /** When the text was last changed. */
  edited?: number;
}

export interface ReviewItem {
  id: string;
  kind: "comment" | "ins" | "del";
  from: number;
  to: number;
  userId: number;
  userName: string;
  ts: number;
  /** 1-based line of `from`. */
  line: number;
  /** Inserted / deleted / commented text. */
  text: string;
  resolved?: boolean;
  resolvedBy?: string | null;
  messages?: ThreadMessage[];
}

/** Collect every review item of a doc with resolved positions. */
export function reviewItems(doc: Y.Doc, ytext: Y.Text): ReviewItem[] {
  const content = ytext.toString();
  const items: Array<Omit<ReviewItem, "line">> = [];
  changesMap(doc).forEach((rec, id) => {
    if (rec.type === "ins") {
      const from = decodeRel(doc, rec.from);
      const to = decodeRel(doc, rec.to);
      if (from === null || to === null || to <= from) return;
      items.push({ id, kind: "ins", from, to, userId: rec.userId, userName: rec.userName, ts: rec.ts, text: content.slice(from, to) });
    } else {
      const pos = decodeRel(doc, rec.pos);
      if (pos === null) return;
      items.push({ id, kind: "del", from: pos, to: pos, userId: rec.userId, userName: rec.userName, ts: rec.ts, text: rec.text ?? "" });
    }
  });
  threadsMap(doc).forEach((thread, id) => {
    const from = decodeRel(doc, thread.get("from") as string);
    const to = decodeRel(doc, thread.get("to") as string);
    if (from === null || to === null) return;
    const messages = ((thread.get("messages") as Y.Array<ThreadMessage> | undefined)?.toArray() ?? []).slice();
    items.push({
      id,
      kind: "comment",
      from,
      to: Math.max(from, to),
      userId: Number(thread.get("userId")),
      userName: String(thread.get("userName") ?? ""),
      ts: Number(thread.get("ts")),
      text: content.slice(from, Math.max(from, to)),
      resolved: !!thread.get("resolved"),
      resolvedBy: (thread.get("resolvedBy") as string | null | undefined) ?? null,
      messages,
    });
  });
  items.sort((a, b) => a.from - b.from || a.ts - b.ts);
  // Items are in document order, so one pass over the text numbers them all.
  let line = 1;
  let at = 0;
  return items.map((item) => {
    for (; at < item.from && at < content.length; at++) if (content.charCodeAt(at) === 10) line++;
    return { ...item, line };
  });
}

/** A review item as the panel shows it: a deletion and an insertion at the same spot, by one person, become one replacement. */
export interface ReviewEntry extends ReviewItem {
  /** The deletion this insertion replaces. */
  replaced?: ReviewItem;
}

/** Every record an entry stands for. */
export const entryIds = (e: ReviewEntry) => (e.replaced ? [e.id, e.replaced.id] : [e.id]);

/** Pair each tracked insertion with a deletion by the same person right at its start or end. */
export function groupReplacements<T extends ReviewItem>(items: T[]): Array<T & ReviewEntry> {
  const used = new Set<string>();
  const out: Array<T & ReviewEntry> = [];
  for (const item of items) {
    if (item.kind !== "ins") continue;
    const del = items.find((d) => d.kind === "del" && !used.has(d.id) && d.userId === item.userId && (d.from === item.from || d.from === item.to));
    if (del) used.add(del.id);
    out.push(del ? { ...item, replaced: del } : item);
  }
  for (const item of items) if (item.kind !== "ins" && !used.has(item.id)) out.push(item);
  return out.sort((a, b) => a.from - b.from || a.ts - b.ts);
}

// ---------------------------------------------------------------- operations

/** Origin of review edits. The doc's undo manager tracks it, so accepting or rejecting can be undone. */
export const REVIEW_ORIGIN = "benleaf-review";
/** Housekeeping that shouldn't land on the undo stack. */
const CLEANUP_ORIGIN = "benleaf-review-cleanup";

export function acceptChange(doc: Y.Doc, id: string) {
  doc.transact(() => changesMap(doc).delete(id), REVIEW_ORIGIN);
}

export function rejectChange(doc: Y.Doc, ytext: Y.Text, id: string) {
  const map = changesMap(doc);
  const rec = map.get(id);
  if (!rec) return;
  doc.transact(() => {
    if (rec.type === "ins") {
      const from = decodeRel(doc, rec.from);
      const to = decodeRel(doc, rec.to);
      if (from !== null && to !== null && to > from) ytext.delete(from, to - from);
    } else {
      const pos = decodeRel(doc, rec.pos);
      if (pos !== null && rec.text) ytext.insert(pos, rec.text);
    }
    map.delete(id);
  }, REVIEW_ORIGIN);
}

export function acceptAll(doc: Y.Doc, ids?: string[]) {
  doc.transact(() => {
    const map = changesMap(doc);
    for (const id of ids ?? [...map.keys()]) map.delete(id);
  }, REVIEW_ORIGIN);
}

export function rejectAll(doc: Y.Doc, ytext: Y.Text, ids?: string[]) {
  const map = changesMap(doc);
  // Reject from the end of the document backwards so positions stay valid.
  const list = (ids ?? [...map.keys()])
    .map((id) => {
      const rec = map.get(id)!;
      const pos = rec ? decodeRel(doc, rec.type === "ins" ? rec.from : rec.pos) ?? 0 : 0;
      return { id, pos };
    })
    .sort((a, b) => b.pos - a.pos);
  doc.transact(() => {
    for (const { id } of list) rejectChange(doc, ytext, id);
  }, REVIEW_ORIGIN);
}

export function addComment(doc: Y.Doc, ytext: Y.Text, from: number, to: number, user: ReviewUser, text: string): string {
  const id = newId();
  doc.transact(() => {
    const thread = new Y.Map<unknown>();
    thread.set("from", encodeRel(ytext, from, 0));
    thread.set("to", encodeRel(ytext, to, -1));
    thread.set("userId", user.id);
    thread.set("userName", user.name);
    thread.set("ts", Date.now());
    thread.set("resolved", false);
    const messages = new Y.Array<ThreadMessage>();
    messages.push([{ id: newId(), userId: user.id, userName: user.name, text, ts: Date.now() }]);
    thread.set("messages", messages);
    threadsMap(doc).set(id, thread);
  }, REVIEW_ORIGIN);
  return id;
}

export function replyToThread(doc: Y.Doc, threadId: string, user: ReviewUser, text: string) {
  const thread = threadsMap(doc).get(threadId);
  const messages = thread?.get("messages") as Y.Array<ThreadMessage> | undefined;
  if (!messages) return;
  doc.transact(() => messages.push([{ id: newId(), userId: user.id, userName: user.name, text, ts: Date.now() }]), REVIEW_ORIGIN);
}

function threadMessages(doc: Y.Doc, threadId: string) {
  return threadsMap(doc).get(threadId)?.get("messages") as Y.Array<ThreadMessage> | undefined;
}

export function editMessage(doc: Y.Doc, threadId: string, messageId: string, text: string) {
  const messages = threadMessages(doc, threadId);
  const i = messages?.toArray().findIndex((m) => m.id === messageId) ?? -1;
  if (!messages || i < 0) return;
  const old = messages.get(i);
  if (old.text === text) return;
  doc.transact(() => {
    messages.delete(i, 1);
    messages.insert(i, [{ ...old, text, edited: Date.now() }]);
  }, REVIEW_ORIGIN);
}

/** Remove one reply. The first message is the comment itself: delete the thread instead. */
export function deleteMessage(doc: Y.Doc, threadId: string, messageId: string) {
  const messages = threadMessages(doc, threadId);
  const i = messages?.toArray().findIndex((m) => m.id === messageId) ?? -1;
  if (!messages || i < 1) return;
  doc.transact(() => messages.delete(i, 1), REVIEW_ORIGIN);
}

export function setResolved(doc: Y.Doc, threadId: string, resolved: boolean, user: ReviewUser) {
  const thread = threadsMap(doc).get(threadId);
  if (!thread) return;
  doc.transact(() => {
    thread.set("resolved", resolved);
    thread.set("resolvedBy", resolved ? user.name : null);
  }, REVIEW_ORIGIN);
}

export function deleteThread(doc: Y.Doc, threadId: string) {
  doc.transact(() => threadsMap(doc).delete(threadId), REVIEW_ORIGIN);
}

/** Remove insertion records whose text has been deleted entirely. */
function gcCollapsed(doc: Y.Doc) {
  const dead: string[] = [];
  changesMap(doc).forEach((rec, id) => {
    if (rec.type !== "ins") return;
    const from = decodeRel(doc, rec.from);
    const to = decodeRel(doc, rec.to);
    if (from === null || to === null || to <= from) dead.push(id);
  });
  if (dead.length) doc.transact(() => dead.forEach((id) => changesMap(doc).delete(id)), CLEANUP_ORIGIN);
}

// ---------------------------------------------------------------- editor extension

export interface ReviewConfig {
  doc: Y.Doc;
  ytext: Y.Text;
  /** Called when the user clicks on a review mark in the editor. */
  onFocusItem?: (id: string) => void;
  /** Called when the user asks to comment on the current selection. */
  onRequestComment?: (from: number, to: number) => void;
  /** Move to the next (1) or previous (-1) comment or change. */
  onNavigate?: (dir: 1 | -1) => void;
  canComment: boolean;
}

export const reviewConfig = Facet.define<ReviewConfig, ReviewConfig>({ combine: (v) => v[v.length - 1] });

/** Tell the editor which thread/change is focused in the panel. */
export const setFocusedItem = StateEffect.define<string | null>();
const refreshReview = StateEffect.define<null>();

const focusedItem = StateField.define<string | null>({
  create: () => null,
  update(value, tr) {
    for (const e of tr.effects) if (e.is(setFocusedItem)) return e.value;
    return value;
  },
});

class DeletionWidget extends WidgetType {
  constructor(
    readonly id: string,
    readonly text: string,
    readonly userName: string,
    readonly focused: boolean,
  ) {
    super();
  }
  eq(other: DeletionWidget) {
    return other.id === this.id && other.text === this.text && other.focused === this.focused;
  }
  toDOM() {
    const span = document.createElement("span");
    const short = this.text.length > 80 && !this.focused;
    span.className = "cm-del-widget";
    span.textContent = short ? this.text.slice(0, 40) + "…" + this.text.slice(-20) : this.text;
    span.title = `Deleted by ${this.userName}`;
    span.dataset.reviewId = this.id;
    return span;
  }
  ignoreEvent() {
    return false;
  }
}

function buildDecorations(view: EditorView): DecorationSet {
  const { doc, ytext } = view.state.facet(reviewConfig);
  const focused = view.state.field(focusedItem);
  const len = view.state.doc.length;
  // Y text and editor text should match; bail out if a sync is mid-flight.
  if (ytext.length !== len) return Decoration.none;
  const items = reviewItems(doc, ytext);
  const ranges: Array<{ from: number; to: number; deco: Decoration }> = [];
  for (const item of items) {
    const from = Math.min(item.from, len);
    const to = Math.min(item.to, len);
    if (item.kind === "ins") {
      ranges.push({
        from,
        to,
        deco: Decoration.mark({
          class: "cm-ins",
          attributes: { style: `--ins-color: ${userColor(item.userId)}`, title: `Inserted by ${item.userName}`, "data-review-id": item.id },
        }),
      });
    } else if (item.kind === "del") {
      ranges.push({ from, to: from, deco: Decoration.widget({ widget: new DeletionWidget(item.id, item.text, item.userName, focused === item.id), side: -1 }) });
    } else if (!item.resolved && to > from) {
      ranges.push({
        from,
        to,
        deco: Decoration.mark({ class: `cm-comment-range${focused === item.id ? " focused" : ""}`, attributes: { "data-review-id": item.id } }),
      });
    }
  }
  ranges.sort((a, b) => a.from - b.from || (a.to === a.from ? -1 : 0) - (b.to === b.from ? -1 : 0));
  const builder = new RangeSetBuilder<Decoration>();
  for (const r of ranges) builder.add(r.from, r.to, r.deco);
  return builder.finish();
}

const reviewPlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    private unobserve: () => void;
    private gcTimer: number | undefined;
    constructor(view: EditorView) {
      this.decorations = buildDecorations(view);
      const { doc } = view.state.facet(reviewConfig);
      let queued = false;
      const refresh = () => {
        if (queued) return;
        queued = true;
        queueMicrotask(() => {
          queued = false;
          view.dispatch({ effects: refreshReview.of(null) });
        });
      };
      const changes = changesMap(doc);
      const threads = threadsMap(doc);
      changes.observe(refresh);
      threads.observeDeep(refresh);
      this.unobserve = () => {
        changes.unobserve(refresh);
        threads.unobserveDeep(refresh);
      };
    }
    update(update: ViewUpdate) {
      const refreshed = update.transactions.some((t) => t.effects.some((e) => e.is(refreshReview) || e.is(setFocusedItem)));
      if (update.docChanged || refreshed) {
        this.decorations = buildDecorations(update.view);
        if (update.docChanged) {
          const { doc } = update.state.facet(reviewConfig);
          clearTimeout(this.gcTimer);
          this.gcTimer = window.setTimeout(() => gcCollapsed(doc), 1500);
        }
      }
    }
    destroy() {
      clearTimeout(this.gcTimer);
      this.unobserve();
    }
  },
  {
    decorations: (v) => v.decorations,
    eventHandlers: {
      mousedown(e, view) {
        const el = (e.target as HTMLElement).closest("[data-review-id]") as HTMLElement | null;
        if (el?.dataset.reviewId) view.state.facet(reviewConfig).onFocusItem?.(el.dataset.reviewId);
        return false;
      },
    },
  },
);

/** Floating "Comment" button over a non-empty selection. */
const commentTooltip = StateField.define<readonly Tooltip[]>({
  create: () => [],
  update(_tips, tr) {
    const conf = tr.state.facet(reviewConfig);
    const sel = tr.state.selection.main;
    if (!conf.canComment || sel.empty) return [];
    return [
      {
        pos: sel.head,
        above: sel.head === sel.from,
        strictSide: false,
        arrow: false,
        create: (view) => {
          const dom = document.createElement("div");
          dom.className = "cm-comment-tip";
          const btn = document.createElement("button");
          btn.className = "btn btn-sm";
          btn.textContent = "💬 Comment";
          btn.onmousedown = (e) => {
            e.preventDefault();
            const s = view.state.selection.main;
            view.state.facet(reviewConfig).onRequestComment?.(s.from, s.to);
          };
          dom.appendChild(btn);
          return { dom };
        },
      },
    ];
  },
  provide: (f) => showTooltip.computeN([f], (state) => state.field(f)),
});

const navigate = (dir: 1 | -1) => (view: EditorView) => {
  const go = view.state.facet(reviewConfig).onNavigate;
  go?.(dir);
  return !!go;
};

export function review(config: ReviewConfig): Extension {
  return [
    reviewConfig.of(config),
    focusedItem,
    reviewPlugin,
    commentTooltip,
    keymap.of([
      // Alongside Mod-Alt-m for "comment". Plain Alt+key types characters on a Mac.
      { key: "Mod-Alt-n", run: navigate(1), preventDefault: true },
      { key: "Mod-Alt-p", run: navigate(-1), preventDefault: true },
    ]),
  ];
}

/** Dispatch a selection + scroll to a review item. */
export function revealItem(view: EditorView, item: Pick<ReviewItem, "from" | "to" | "id">) {
  const len = view.state.doc.length;
  const from = Math.min(item.from, len);
  const to = Math.min(item.to, len);
  view.dispatch({
    selection: { anchor: from, head: to },
    effects: [EditorView.scrollIntoView(from, { y: "center" }), setFocusedItem.of(item.id)],
    annotations: reviewAction.of(true),
  });
}

export type { ChangeRecord };
